import React, { useState, useMemo } from "react";
import moment from "moment";
import { uniq } from "lodash";
import FileStats from "../FileStats";
import EntitySummary from "../EntitySummary";
import SummaryCard from "./SummaryCard";
import { wholeNumberWithCommas } from "../../utils/NumberFormatter";
import buildSummaryData from "./buildSummaryData";
import { CheckCircleOutlined, ExpandMore, ExpandLess } from "@mui/icons-material";
import { MappingWarningIcon } from "./MappingIcons";
import AmgenNotice, { showAmgenWarning } from "./AmgenNotice";

const SubmissionSummary = ({
  submissions,
  submissionType,
  currentUser,
  onViewFile,
  submissionName,
}) => {
  const [showSelectedEntities, setShowSelectedEntities] = useState(true);
  // ENTITY FILTERING DISABLED
  // const [showExcludedEntities, setShowExcludedEntities] = useState(false);
  const summaryData = useMemo(() => buildSummaryData(submissions), [submissions]);

  if (!submissions.length) {
    return <></>;
  }

  const handleShowSelectedEntities = () => {
    setShowSelectedEntities(!showSelectedEntities);
  };

  // ENTITY FILTERING DISABLED
  // const handleShowExcludedEntities = () => {
  //   setShowExcludedEntities(!showExcludedEntities);
  // };

  const renderEntities = ({ entities, showWarning = false }) => {
    return entities.map((ce) => (
      <EntitySummary
        key={ce.id_340b}
        title={<div className="entity_summary__title">{ce.id_340b}</div>}
        subtitle={<div className="entity_summary__subtitle">340B ID</div>}
        stats={[
          { title: "Manufacturers", count: ce.manufacturers.length },
          { title: "Products", count: ce.products.length },
          { title: "NDCs", count: Object.keys(ce.ndcs).length },
        ]}
        table={
          <table className="summary_table">
            <thead>
              <tr>
                <th>Manufacturer</th>
                <th>Product</th>
                <th>NDC</th>
                <th>Count</th>
              </tr>
            </thead>
            <tbody>
              {Object.values(ce.ndcs).map((ndcDetails) => (
                <tr key={ndcDetails.ndc}>
                  <td>{ndcDetails.manufacturer}</td>
                  <td>{ndcDetails.product_name}</td>
                  <td>{ndcDetails.ndc}</td>
                  <td>{wholeNumberWithCommas(ndcDetails.rows)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        }
        numberColumns={4}
        rowCount={ce.rowCount}
        showWarning={showWarning}
      />
    ));
  };

  return (
    <div className="submission_preview__container">
      <div className="submission_preview">
        <div className="submission_preview__header">
          <div className="submission_preview__header__details">
            <div className="submission_preview__subtitle">Submission Summary</div>
            <div className="submission_preview__header__title">{submissionName}</div>
            <div className="submission_preview__header__subtitle">
              Date: {moment().format("MMMM D, YYYY")}
            </div>
          </div>
          <div className="submission_preview__header__user">
            <div className="submission_preview__user__avatar">
              <div className="submission_preview__user__avatar__initials">
                {currentUser.initials}
              </div>
            </div>
            <div className="submission_preview__user__text">
              <div className="submission_preview__user__text__title">{currentUser.full_name}</div>
              <div className="submission_preview__user__text__subtitle">{currentUser.email}</div>
            </div>
          </div>
        </div>

        <div className="submission_preview__divider"></div>
        <div className="flex flex-column gap--16">
          <div className="submission_preview__content">
            <SummaryCard
              title="Original Rows"
              description="Total rows uploaded across all files"
              value={wholeNumberWithCommas(summaryData.original)}
            />
            <SummaryCard
              title="Selected Rows"
              // ENTITY FILTERING DISABLED was "Rows with valid NDCs and 340BID permissions"
              description="Rows with valid NDCs"
              value={wholeNumberWithCommas(summaryData.rows)}
            />
            <SummaryCard
              title="Rows for Submission"
              description="Selected rows with no mapping errors"
              value={wholeNumberWithCommas(summaryData.valid)}
            />
          </div>
          <div className="submission_preview__content">
            <SummaryCard
              title="Entities"
              description="Number of entities found in submission"
              value={wholeNumberWithCommas(summaryData.entities.length)}
            />
            <SummaryCard
              title="Manufacturers"
              description="Number of manufacturers found in submission"
              value={wholeNumberWithCommas(summaryData.manufacturers)}
            />
            <SummaryCard
              title="NDCs"
              description="Number of unique NDCs found in submission"
              value={wholeNumberWithCommas(uniq(summaryData.products).length)}
            />
          </div>
        </div>

        <div className="submission_preview__divider">
          <div className="submission_preview__divider__title">Files</div>
        </div>
        {showAmgenWarning(submissionType, submissions) && (
          <AmgenNotice submissionType={submissionType} stackText />
        )}
        <FileStats
          submissions={submissions}
          submissionType={submissionType}
          onViewFile={onViewFile}
        />

        {/* ENTITY FILTERING DISABLED the excludedEntities half of this condition was:
            (summaryData?.entities?.length > 0 || summaryData?.excludedEntities?.length > 0) */}
        {summaryData?.entities?.length > 0 && (
          <div className="submission_preview__divider">
            <div className="submission_preview__divider__title">Covered Entities</div>
          </div>
        )}
        <div className="submission_preview__entities">
          {/* ENTITY FILTERING DISABLED 
          {summaryData?.excludedEntities?.length > 0 && (
            <div className="submission_preview__entities__section">
              <div className="submission_preview__entities__header">
                <div className="submission_preview__entities__section__title">
                  Entities Not Selected ({summaryData.excludedEntities.length})
                </div>
                <div className="button button--white submission_preview__expand"  onClick={handleShowSelectedEntities}>
                  {showExcludedEntities ? (
                    <ExpandLess style={{ fontSize: "18px" }} />
                  ) : (
                    <ExpandMore style={{ fontSize: "18px" }} />
                  )}
                </div>
              </div>
              <div className="submission_preview__notice submission_preview__notice--warning">
                <MappingWarningIcon />
                <div>
                  {wholeNumberWithCommas(summaryData.excludedRows)}{" "}
                  {summaryData.excludedRows === 1 ? "row was" : "rows were"} excluded because they
                  belong to entities you do not have access to. These rows will not be submitted.
                </div>
              </div>
              {showExcludedEntities &&
                renderEntities({ entities: summaryData?.excludedEntities, showWarning: true })}
            </div>
          )}
          */}
          {summaryData?.entities?.length > 0 && (
            <div className="submission_preview__entities__section">
              <div className="submission_preview__entities__header">
                <div className="submission_preview__entities__section__title">
                  {/* ENTITY FILTERING DISABLED was "Selected Entities ({n})" */}
                  Entities ({summaryData.entities.length})
                </div>
                <div
                  className="button button--white submission_preview__expand"
                  onClick={handleShowSelectedEntities}
                >
                  {showSelectedEntities ? (
                    <ExpandLess style={{ fontSize: "18px" }} />
                  ) : (
                    <ExpandMore style={{ fontSize: "18px" }} />
                  )}
                </div>
              </div>
              {/* ENTITY FILTERING DISABLED 
              <div className="submission_preview__notice">
                <div className="submission_preview__notice__icon">
                  <CheckCircleOutlined />
                </div>
                <div>
                  {wholeNumberWithCommas(summaryData.rows)}{" "}
                  {summaryData.rows === 1 ? "row has" : "rows have"} been selected because they
                  belong to your entities.
                </div>
              </div>
              */}
              {showSelectedEntities && renderEntities({ entities: summaryData?.entities })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SubmissionSummary;
